interface TableRow {
  label?: string;
  shank_type?: string;
  "ISO NO."?: string;
  "length(mm)"?: string;
  values: Record<string, string>;
}

interface TableData {
  rows: TableRow[];
}

interface CatalogItem {
  id: string;
  product_name?: string;
  category?: string;
  full_table_data?: TableData;
  description?: string;
  shape?: string;
  packing?: string;
  sub_models_count?: number;
}

document.addEventListener("DOMContentLoaded", async () => {
  try {
    const params = new URLSearchParams(window.location.search);
    // fetch index.json first to resolve the actual category name for image paths
    const indexRes = await fetch("/catalog/catalog_sheets/index.json?t=" + new Date().getTime(), {
      cache: "no-store",
    });
    if (!indexRes.ok) throw new Error("Failed to fetch category index");
    const categories: CategoryIndex[] = await indexRes.json();

    const category = params.get("category");
    if (!category) {
      renderAllCategories(categories);
      return;
    }

    const currentCat = categories.find((c) => c.id === category);
    const categoryName = currentCat ? currentCat.name : category;

    const response = await fetch(
      `/catalog/catalog_sheets/${category}.json?t=` + new Date().getTime(),
      { cache: "no-store" },
    );
    if (!response.ok) throw new Error(`Failed to fetch JSON: ${response.status}`);
    const data: CatalogItem[] = await response.json();
    renderCatalog(data, categoryName);
  } catch (error) {
    console.error("Error loading catalog:", error);
    const container = document.getElementById("catalog-container");
    if (container) {
      container.innerHTML = '<div class="loading">Failed to load catalog data.</div>';
    }
  }
});

function parseProductName(fullName: string): string {
  if (!fullName) return "Dental Bur";
  const englishMatch = fullName.match(/[A-Z\s()]+/g);
  let title = "Dental Bur";
  if (englishMatch && englishMatch.length > 0) {
    title = englishMatch.reduce((a, b) => (a.length > b.length ? a : b)).trim();
    title = title.replace(/^[)\s]+/, "");
  }
  return title;
}

function renderCatalog(items: CatalogItem[], categoryName: string) {
  const container = document.getElementById("catalog-container");
  if (!container) return;
  container.innerHTML = "";

  items.forEach((item: CatalogItem) => {
    const shortCode = item.id || "BUR";
    const title = parseProductName(item.product_name || "");

    const card = document.createElement("div");
    card.className = "product-card";

    const tableData = item.full_table_data;
    if (!tableData || !tableData.rows || tableData.rows.length < 3) return;

    // Assuming first two rows are Diameter and Length
    const rowDiameter = tableData.rows[0];
    const rowLength = tableData.rows[1];

    // Use the shank_type of the row to identify shank rows, or just slice from index 2
    const shankRows = tableData.rows.filter((r) => r.shank_type);

    // Infer Order NO. keys from rowDiameter values and sort them logically
    // JSON.parse reorders object keys that look like integers, so we must sort them back logically.
    function parseOrderValue(val: string | number): number {
      let s = String(val).trim();
      if (s.includes(" ")) {
        // e.g. "33 1/2" -> 33.5
        let parts = s.split(" ");
        let whole = parseFloat(parts[0]);
        let fracParts = parts[1].split("/");
        let frac = parseFloat(fracParts[0]) / parseFloat(fracParts[1]);
        return whole + frac;
      } else if (s.includes("/")) {
        // e.g. "1/4" -> 0.25
        let parts = s.split("/");
        return parseFloat(parts[0]) / parseFloat(parts[1]);
      }
      // e.g. "330", "1", "2"
      return parseFloat(s);
    }

    let usNos = Object.keys(rowDiameter.values).sort((a, b) => {
      return parseOrderValue(a) - parseOrderValue(b);
    });

    // Build HTML
    let tableHTML = `
            <table class="product-table">
                <thead>
                    <tr>
                        <th rowspan="2" class="title-cell"><span>${shortCode}</span></th>
                        <th class="prop-label">${rowDiameter.label || "Diameter (mm)"}</th>
                        ${usNos.map((no) => `<th>${rowDiameter.values[no] || ""}</th>`).join("")}
                    </tr>
                    <tr>
                        <th class="prop-label">${rowLength.label || "Length (mm)"}</th>
                        ${usNos.map((no) => `<th>${rowLength.values[no] || ""}</th>`).join("")}
                    </tr>
                    <tr class="header-row">
                        <th class="col-shank">Shank Type</th>
                        <th class="split-cell">
                            <svg class="split-line" preserveAspectRatio="none" viewBox="0 0 100 100">
                                <line x1="0" y1="0" x2="100" y2="100"></line>
                            </svg>
                            <span class="split-us">Order NO.</span>
                            <span class="split-iso">ISO No.</span>
                        </th>
                        ${usNos.map((no) => `<th class="us-no-header">${no}</th>`).join("")}
                    </tr>
                </thead>
                <tbody>
                    ${shankRows
                      .map((row) => {
                        // Some rows might be missing values for some US Nos. Replace undefined with empty string.
                        return `
                        <tr>
                            <td class="shank-name">${row.shank_type}${row["length(mm)"] ? ` (${row["length(mm)"]}mm)` : ""}</td>
                            <td class="iso-no">${row["ISO NO."] || ""}</td>
                            ${usNos.map((no) => `<td>${row.values[no] || ""}</td>`).join("")}
                        </tr>
                        `;
                      })
                      .join("")}
                </tbody>
            </table>
        `;

    card.innerHTML = `
        <div class="product-top">
            <div class="product-left">
                <div class="product-title">${title}</div>
                <div class="img-container">
                    <img src="/catalog/catalog_sheets/burs_img/${categoryName}/${shortCode}.png" class="product-image" alt="${title}" onerror="this.onerror=null; this.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2U4ZThlOCIvPjxwYXRoIGQ9Ik0yNSA3NWw1MC01MEwyNSA1MHoiIGZpbGw9IiNjY2MiLz48L3N2Zz4=';" />
                </div>
            </div>
            <div class="product-right">
                <div class="table-scroll-container">
                    ${tableHTML}
                </div>
            </div>
        </div>
        <div class="product-bottom">
            <details class="product-details">
                <summary>Specifications & Details</summary>
                <div class="details-content">
                    <table class="details-table">
                        <tbody>
                            ${item.category ? `<tr><th>Category</th><td>${escapeCatalogHtml(item.category)}</td></tr>` : ""}
                            ${item.shape ? `<tr><th>Shape</th><td>${escapeCatalogHtml(item.shape).replace(/\n/g, "<br>")}</td></tr>` : ""}
                            ${item.description ? `<tr><th>Description</th><td>${escapeCatalogHtml(item.description).replace(/\n/g, "<br>")}</td></tr>` : ""}
                            ${item.packing ? `<tr><th>Packing</th><td>${escapeCatalogHtml(item.packing).replace(/\n/g, "<br>")}</td></tr>` : ""}
                            ${item.sub_models_count ? `<tr><th>Sub-Models</th><td>${item.sub_models_count}</td></tr>` : ""}
                        </tbody>
                    </table>
                </div>
            </details>
        </div>
        `;

    container.appendChild(card);
  });

  // Setup Image Zoom Modal
  setupImageModal();
}

function setupImageModal() {
  let modal = document.getElementById("image-modal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "image-modal";
    modal.className = "image-modal";
    modal.innerHTML = `
      <span class="image-modal-close" id="image-modal-close">&times;</span>
      <img class="image-modal-content" id="image-modal-img" />
    `;
    document.body.appendChild(modal);

    const closeModal = () => {
      if (modal) {
        modal.classList.remove("show");
        setTimeout(() => {
          if (modal) modal.style.display = "none";
        }, 300);
      }
    };

    modal.addEventListener("click", (e) => {
      if (e.target === modal || (e.target as HTMLElement).id === "image-modal-close") {
        closeModal();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    });
  }

  const modalImg = document.getElementById("image-modal-img") as HTMLImageElement;
  const images = document.querySelectorAll(".product-image");
  images.forEach((img) => {
    // Add pointer cursor to indicate clickability
    (img as HTMLElement).style.cursor = "zoom-in";
    img.addEventListener("click", () => {
      if (modal && modalImg) {
        modal.style.display = "flex";
        // Force reflow
        void modal.offsetWidth;
        modalImg.src = (img as HTMLImageElement).src;
        modal.classList.add("show");
      }
    });
  });
}

function renderAllCategories(categories: CategoryIndex[]) {
  const container = document.getElementById("catalog-container");
  if (!container) return;

  container.innerHTML = "";

  if (categories.length === 0) {
    container.innerHTML = '<div class="empty-msg">No product categories available.</div>';
    return;
  }

  const headerHtml = `
    <div style="text-align: center; margin-bottom: 2rem;">
      <h2 style="font-size: 2rem; color: var(--cyan-700);">All Categories</h2>
      <p style="color: var(--text-muted); font-size: 1.1rem;">Select a product category to view our complete range</p>
    </div>
  `;
  container.innerHTML = headerHtml;

  const grid = document.createElement("div");
  grid.className = "category-grid";

  categories.forEach((category) => {
    const link = document.createElement("a");
    link.href = `?category=${encodeURIComponent(category.id)}`;
    link.className = "category-card";

    link.innerHTML = `
      <div class="category-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
          <line x1="12" y1="22.08" x2="12" y2="12"></line>
        </svg>
      </div>
      <h3>${escapeCatalogHtml(category.name)}</h3>
    `;

    grid.appendChild(link);
  });

  container.appendChild(grid);
}

function escapeCatalogHtml(unsafe: string) {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
